// ---------- la copie en ligne de la progression ----------
// Tient la progression de l'appareil (src/store.ts) et celle du compte (backend/src/routes/
// account.ts) au même point. Trois moments, comme dans Codigrid :
//
//   lancement   syncOnce()  — tirer la sauvegarde du compte, la réunir, renvoyer ce qui manque
//   en jeu      pushSoon()  — chaque écriture du store, regroupées
//   départ      flushSync() — l'appli passe en arrière-plan : on vide la file tout de suite
//
// Rien ici n'a le droit de casser le jeu. Chaque chemin avale ses erreurs : un Worker injoignable,
// un quota plein ou un jeton révoqué coûtent la copie en ligne, jamais la partie en cours.

import { Unauthorized, currentUser, ensureUser, fetchSave, forgetUser, pushSave } from './account';
import { adoptSave, localSave, starsOf, watchSaves, type Changes } from './store';
import type { SaveState } from '../shared/save';

/**
 * Le délai de regroupement. Une ligne remplie, ce sont six ou huit frappes en quelques secondes,
 * et chacune réécrit la partie : envoyer à chaque frappe ferait huit requêtes pour un seul état
 * qui compte, le dernier.
 *
 * Deux minutes SANS SAISIE (le délai repart à chaque frappe), et deux contraintes le fixent :
 *  - la RADIO (Codigrid l'a mesuré, son src/sync.ts) : un envoi tire le modem de sa veille pour
 *    une dizaine de secondes ; des envois rapprochés ne le laisseraient jamais redescendre ;
 *  - le BUDGET : 100 000 requêtes Worker par jour pour tout le monde. À vingt secondes, une partie
 *    de quelques minutes coûtait une dizaine d'envois, soit ~95 requêtes par joueur assidu et par
 *    jour — le plafond vers un millier de joueurs. Ici, une partie jouée d'une traite part UNE
 *    fois, quand elle est gagnée (sans attendre), et la file se vide quand on la quitte
 *    (`flushSync`, src/main.ts) ou qu'on change d'application.
 *
 * Rien n'est risqué à attendre : la sauvegarde locale est intacte pendant ce temps, `flushSync()`
 * vide la file quand le joueur change d'application, et le prochain lancement rattrape ce qu'un
 * arrêt brutal aurait perdu (cf. `run`, qui renvoie tout ce que le serveur n'a pas).
 */
const PUSH_MS = 120_000;

/** Un envoi ne porte pas plus de parties que ça : le Worker en refuse au-delà de 200 (cf.
 *  backend/src/validation.ts, `parseSavePush`), et une seule requête énorme qui échoue fait tout
 *  repartir au lancement suivant. */
const CHUNK = 100;

/** Un seul re-fabrication de compte par session : un jeton refusé est remplacé une fois, jamais
 *  en boucle contre un Worker qui répondrait 401 à tout. */
let reminted = false;

async function recover(err: unknown): Promise<boolean> {
  if (!(err instanceof Unauthorized) || reminted) return false;
  reminted = true;
  forgetUser();
  return !!(await ensureUser());
}

/** Ce qui attend de partir, par identifiant. L'état envoyé est relu AU MOMENT d'envoyer (`localSave`),
 *  donc une partie qui a encore bougé dans la fenêtre part dans son dernier état. */
const queued: Changes = { puzzles: [], chapters: [], streak: false };
let timer = 0;

function enqueue(c: Changes): void {
  for (const id of c.puzzles) if (!queued.puzzles.includes(id)) queued.puzzles.push(id);
  for (const id of c.chapters) if (!queued.chapters.includes(id)) queued.chapters.push(id);
  queued.streak ||= c.streak;
}

function take(): Changes {
  const out: Changes = { ...queued, puzzles: [...queued.puzzles], chapters: [...queued.chapters] };
  queued.puzzles = [];
  queued.chapters = [];
  queued.streak = false;
  return out;
}

async function send(save: SaveState): Promise<void> {
  if (!Object.keys(save.puzzles).length && !Object.keys(save.skipped).length && !save.streak)
    return;
  let token = await ensureUser();
  if (!token) throw new Error('offline');
  let merged: SaveState;
  try {
    merged = await pushSave(token, save);
  } catch (err) {
    if (!(await recover(err))) throw err;
    token = currentUser();
    if (!token) throw err;
    merged = await pushSave(token, save);
  }
  // Ce qui revient est la fusion avec ce que le serveur avait : si un autre appareil y a mis
  // mieux (une partie finie là-bas), il arrive ici. Ce que l'appareil a joué PENDANT la requête
  // n'est pas écrasé — sa partie est plus récente, la fusion la garde.
  adoptSave(merged);
}

/** Envoie, par paquets, puis rend à la file ce qui n'est pas parti : la prochaine écriture ou le
 *  prochain lancement le reprendra. */
/** Coupée pour de bon : « Supprimer mes données » efface le compte, puis recharge la page. Entre
 *  les deux, le `pagehide` du rechargement vidait la file vers… un compte neuf, que `ensureUser`
 *  fabriquait aussitôt — la suppression recréait ce qu'elle venait d'effacer. */
let stopped = false;
export function stopSync(): void {
  stopped = true;
  clearTimeout(timer);
  take();
}

async function flush(c: Changes): Promise<void> {
  if (stopped) return;
  const all = localSave(c);
  const ids = Object.keys(all.puzzles);
  for (let i = 0; i < Math.max(1, ids.length); i += CHUNK) {
    const part = ids.slice(i, i + CHUNK);
    const first = i === 0;
    const save: SaveState = {
      puzzles: Object.fromEntries(part.map((id) => [id, all.puzzles[id]])),
      skipped: first ? all.skipped : {},
      ...(first && all.streak ? { streak: all.streak } : {}),
    };
    try {
      await send(save);
    } catch {
      enqueue({
        puzzles: ids.slice(i),
        chapters: first ? c.chapters : [],
        streak: first && c.streak,
      });
      return;
    }
  }
}

function pushSoon(c: Changes, now = false): void {
  enqueue(c);
  clearTimeout(timer);
  // Une partie finie saute la file : c'est l'état qu'on ne veut jamais perdre, et c'est aussi le
  // dernier que le joueur produira avant, peut-être, de fermer l'appli.
  if (now) return void flush(take());
  timer = window.setTimeout(() => void flush(take()), PUSH_MS);
}

/** Vide la fenêtre de regroupement tout de suite. */
export function flushSync(): void {
  clearTimeout(timer);
  if (queued.puzzles.length || queued.chapters.length || queued.streak) void flush(take());
}

let once: Promise<boolean> | null = null;
let onRemote: () => void = () => {};

/**
 * La passe de lancement, mémoïsée. Rend `true` si l'écran a quelque chose de neuf à montrer.
 *
 * Elle TIRE la sauvegarde du compte et la réunit à celle de l'appareil, puis elle POUSSE ce que
 * l'appareil sait de plus : une partie jouée hors ligne, la progression d'un joueur d'avant les
 * comptes, ou ce qu'un arrêt brutal n'a pas laissé partir. C'est ce qui rend le regroupement sans
 * risque : rien n'est perdu tant que le localStorage tient.
 *
 * Tout tirer d'un coup, et non partie par partie comme Codigrid : une sauvegarde ici fait quelques
 * centaines d'octets (des lettres), pas une liste de dizaines de milliers de taps. Cent parties
 * finies tiennent dans ce que pèse une seule image du jeu.
 */
export function syncOnce(): Promise<boolean> {
  return (once ??= run());
}

async function run(): Promise<boolean> {
  const local = localSave();
  const hasLocal =
    Object.keys(local.puzzles).length > 0 ||
    Object.keys(local.skipped).length > 0 ||
    !!local.streak;
  // Pas de compte et rien à sauver : on n'en crée pas un pour poser une question vide.
  const token = hasLocal ? await ensureUser() : currentUser();
  if (!token) {
    // Un échec ne doit pas tenir toute la session (même leçon que Codigrid) : `once` remis à zéro,
    // le prochain appel repartira — au retour du réseau, ou à l'ouverture du panneau du compte.
    once = null;
    return false;
  }
  try {
    const { changed, ahead } = adoptSave(await fetchSave(token));
    if (ahead.puzzles.length || ahead.chapters.length || ahead.streak) await flush(ahead);
    return changed;
  } catch (err) {
    await recover(err);
    once = null;
    return false;
  }
}

/**
 * Tout refaire depuis zéro : le jeton vient de changer, un code de reprise a été échangé (src/
 * account.ts, `adoptUser`). Rien d'autre n'a eu besoin d'être écrit pour la reprise : `run()` fait
 * déjà ce qu'elle demande — tirer la progression du compte retrouvé, puis lui pousser ce que cet
 * appareil a joué de son côté. La fusion (shared/save.ts) n'en perd rien.
 */
export async function resyncAll(): Promise<boolean> {
  once = null;
  reminted = false;
  // La file part d'abord vers l'ANCIEN compte serait une erreur : elle partira vers le nouveau,
  // avec le reste, depuis `run()`.
  clearTimeout(timer);
  take();
  return syncOnce();
}

/**
 * Démarre la synchro. `refresh` redessine l'écran quand la passe de lancement a ramené du neuf —
 * une partie finie sur l'autre téléphone doit apparaître sur le chemin sans relancer l'appli.
 */
export function startSync(refresh: () => void): void {
  onRemote = refresh;
  for (const id of Object.keys(localSave().puzzles)) known.set(id, starsOf(id));
  // `map` puis `some`, et non `some` seul : chaque partie touchée doit mettre son compte à jour.
  watchSaves((c) => pushSoon(c, c.puzzles.map(won).some(Boolean)));
  addEventListener('pagehide', flushSync);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushSync();
    // Revenir sur l'appli après une coupure : c'est le moment où le réseau revient, et la passe
    // de lancement, si elle a échoué, n'a jamais eu sa seconde chance. Seulement dans ce cas :
    // réussie, elle est mémoïsée et ne redemande rien.
    else if (!once) void syncOnce().then((ch) => ch && onRemote());
  });
  void syncOnce().then((ch) => ch && onRemote());
}

/** Les étoiles de chaque partie au dernier passage : une partie qui vient d'en GAGNER (finie, ou
 *  mieux finie) part tout de suite ; une partie finie qu'on rejoue suit le rythme ordinaire. */
const known = new Map<string, number>();
function won(id: string): boolean {
  const before = known.get(id) ?? 0;
  const now = starsOf(id);
  known.set(id, now);
  return now > before;
}
